import type { Colony, ColonyEvent, ISODate, TreatmentEvent } from './model';
import { read, tx } from './db';
import { inSeason, seasonOf } from './season';
import { honeyShare } from './yearly';
import { feedingSugarKg, infestationPercent, isMiteFall, mitesPerDay, todayISO, withdrawalUntil } from './rules';

const byDateDesc = (a: ColonyEvent, b: ColonyEvent) =>
  b.date.localeCompare(a.date) || b.createdAt.localeCompare(a.createdAt);

export const naturalLabel = (a: Colony, b: Colony) =>
  (a.label ?? '').localeCompare(b.label ?? '', 'cs', { numeric: true });

export async function colonyHistory(colonyId: string): Promise<ColonyEvent[]> {
  const evs = await read.byIndex('events', 'colonyIds', colonyId);
  return evs.sort(byDateDesc);
}

export async function apiaryColonies(apiaryId: string, includeInactive = false): Promise<Colony[]> {
  const cs = await read.byIndex('colonies', 'apiaryId', apiaryId);
  return cs.filter((c) => includeInactive || c.status === 'active').sort(naturalLabel);
}

export interface ColonySummary {
  lastInspection?: ISODate;
  queenCells?: string;
  /** Poslední měření varroázy jako lidský text. */
  /** `fall` = spad roztočů (podložka, po ošetření); jinak test na vzorku včel. */
  lastVarroa?: { date: ISODate; text: string; alarm: boolean; miteCount: number; fall: boolean };
  withdrawalUntil?: ISODate;
  harvestKgThisYear: number;
  /** Krmení v aktuálním roce v kg cukru (přepočteno podle druhu krmiva). */
  feedSugarKgThisYear: number;
  feedingsThisYear: number;
  treatmentsThisYear: number;
  /** Počet všech léčení včelstva (za celou dobu). */
  treatmentsTotal: number;
  harvestsThisYear: number;
  lastHarvestDate?: ISODate;
  lastFeedingDate?: ISODate;
  /** Poslední léčení (za celou dobu). */
  lastTreatment?: { date: ISODate; product: string };
}

/** Souhrn pro kartu včelstva v seznamu. */
export function summarize(events: ColonyEvent[], today: ISODate = todayISO(), seasonStartMonth = 1, colonyId?: string): ColonySummary {
  const cur = seasonOf(today, seasonStartMonth);
  const inCur = (d: ISODate) => inSeason(d, cur);
  const s: ColonySummary = { harvestKgThisYear: 0, feedSugarKgThisYear: 0, feedingsThisYear: 0, treatmentsThisYear: 0, treatmentsTotal: 0, harvestsThisYear: 0 };
  for (const e of [...events].sort(byDateDesc)) {
    if (e.type === 'inspection' && !s.lastInspection) {
      s.lastInspection = e.date;
      if (e.queenCells && e.queenCells !== 'none') s.queenCells = e.queenCells;
    }
    if (e.type === 'varroa_check' && e.method !== 'winter_debris' && !s.lastVarroa) {
      const perDay = mitesPerDay(e);
      const pct = infestationPercent(e);
      if (perDay != null) {
        s.lastVarroa = { date: e.date, text: `spad ${fmt(perDay)}/den`, alarm: perDay >= 10, miteCount: e.miteCount, fall: isMiteFall(e) };
      } else if (pct != null) {
        s.lastVarroa = { date: e.date, text: `${fmt(pct)} % napadení`, alarm: pct >= 3, miteCount: e.miteCount, fall: isMiteFall(e) };
      } else {
        const n = e.miteCount;
        s.lastVarroa = { date: e.date, text: `${n} ${n === 1 ? 'roztoč' : n >= 2 && n <= 4 ? 'roztoči' : 'roztočů'}`, alarm: false, miteCount: e.miteCount, fall: isMiteFall(e) };
      }
    }
    if (e.type === 'treatment') {
      const until = withdrawalUntil(e);
      if (until && until >= today && e.date <= today && (!s.withdrawalUntil || until > s.withdrawalUntil)) {
        s.withdrawalUntil = until;
      }
    }
    if (e.type === 'feeding' && !s.lastFeedingDate) s.lastFeedingDate = e.date;
    if (e.type === 'harvest' && !s.lastHarvestDate) s.lastHarvestDate = e.date;
    if (e.type === 'harvest' && inCur(e.date)) s.harvestsThisYear++;
    if (e.type === 'feeding' && inCur(e.date)) {
      s.feedingsThisYear++;
      s.feedSugarKgThisYear += feedingSugarKg(e);
    }
    if (e.type === 'treatment') {
      s.treatmentsTotal++;
      if (!s.lastTreatment) s.lastTreatment = { date: e.date, product: e.productName };
      if (inCur(e.date)) s.treatmentsThisYear++;
    }
    if (e.type === 'harvest' && inCur(e.date)) {
      s.harvestKgThisYear += honeyShare(e, colonyId ?? e.colonyIds[0]);
    }
  }
  return s;
}

/** Souhrny všech včelstev najednou (jedno čtení tabulky událostí). */
export async function summariesFor(colonyIds: string[], seasonStartMonth = 1): Promise<Record<string, ColonySummary>> {
  // zápisy jen těchto včelstev přes index – nemusí se číst celá databáze
  const lists = await Promise.all(colonyIds.map((id) => read.byIndex('events', 'colonyIds', id)));
  const out: Record<string, ColonySummary> = {};
  for (const [i, id] of colonyIds.entries()) {
    const mine = lists[i];
    const s = summarize(mine, todayISO(), seasonStartMonth);
    // u medobraní za více včelstev počítej podíl právě tohoto včelstva
    const cur = seasonOf(todayISO(), seasonStartMonth);
    s.harvestKgThisYear = mine.reduce((sum, e) => (e.type === 'harvest' && inSeason(e.date, cur) ? sum + honeyShare(e, id) : sum), 0);
    out[id] = s;
  }
  return out;
}

export async function treatmentsInYear(year: number): Promise<TreatmentEvent[]> {
  return tx(['events'], 'readonly', (t) => t.byIndex('events', 'type', 'treatment')).then((xs) =>
    (xs as TreatmentEvent[])
      .filter((e) => !e.deletedAt && e.date.startsWith(String(year)))
      .sort((a, b) => a.date.localeCompare(b.date)),
  );
}

export function fmt(n: number, digits = 1): string {
  // chybějící číslo (např. ručně upravená záloha) nesmí shodit obrazovku
  if (typeof n !== 'number' || !Number.isFinite(n)) return '–';
  return n.toLocaleString('cs-CZ', { maximumFractionDigits: digits });
}
